import React from 'react';
import HelpArticle from '../../components/help/HelpArticle';

const CancellationPolicy: React.FC = () => {
  const content = (
    <>
      <h2>Afbestillingspolitik</h2>
      <p>
        Forstå reglerne for afbestilling og hvad der sker hvis du eller ejeren bliver nødt til at annullere en booking.
      </p>

      <h3>Som lejer: Afbestilling af din booking</h3>
      <p>
        Hvis du har brug for at afbestille en booking, afhænger konsekvenserne af timingen.
      </p>

      <h4>Før booking er bekræftet</h4>
      <p>
        Hvis ejeren endnu ikke har accepteret:
      </p>
      <ul>
        <li>Du kan trække din anmodning tilbage når som helst</li>
        <li>Ingen konsekvenser</li>
        <li>Gå til booking og klik "Annullér anmodning"</li>
      </ul>

      <h4>Mere end 48 timer før afhentning</h4>
      <p>
        Hvis du afbestiller i god tid:
      </p>
      <ul>
        <li>Fuld refusion hvis du har forudbetalt</li>
        <li>Ingen gebyrer</li>
        <li>Ingen negativ påvirkning af din rating</li>
        <li>Ejeren kan bebooke værktøjet til andre</li>
      </ul>

      <h4>Mindre end 48 timer før afhentning</h4>
      <p>
        Sen afbestilling kan medføre:
      </p>
      <ul>
        <li>Muligt gebyr afhængigt af ejerens politik</li>
        <li>Det kan påvirke din rating</li>
        <li>Ejeren har måske afvist andre for din skyld</li>
      </ul>

      <h4>Efter afhentning</h4>
      <p>
        Hvis du allerede har hentet værktøjet:
      </p>
      <ul>
        <li>Du skal stadig betale for lejeperioden</li>
        <li>Kontakt ejeren om tidlig returnering</li>
        <li>Nogle ejere refunderer ubrugte dage</li>
      </ul>

      <h3>Sådan afbestiller du</h3>
      <p>
        Følg disse trin:
      </p>

      <h4>Trin 1: Find bookingen</h4>
      <ul>
        <li>Gå til "Mine bookinger"</li>
        <li>Find den relevante booking</li>
        <li>Klik for at åbne detaljer</li>
      </ul>

      <h4>Trin 2: Informér ejeren FØRST</h4>
      <p>
        <strong>Vigtigt:</strong> Send altid en besked til ejeren først:
      </p>
      <ul>
        <li>Forklar hvorfor du afbestiller</li>
        <li>Vær høflig og undskyldende</li>
        <li>Giv så meget varsel som muligt</li>
      </ul>
      <blockquote>
        "Hej [Navn], jeg er desværre nødt til at afbestille min booking af [værktøj] d. [dato]. [Kort forklaring]. Beklager ulejligheden!"
      </blockquote>

      <h4>Trin 3: Annullér i systemet</h4>
      <ul>
        <li>Klik "Annullér booking"</li>
        <li>Bekræft din afbestilling</li>
        <li>Ejeren modtager automatisk besked</li>
      </ul>

      <h4>Trin 4: Håndter betaling</h4>
      <ul>
        <li>Hvis du har forudbetalt, aftal refusion med ejeren</li>
        <li>Følg ejerens betalingspolitik</li>
      </ul>

      <h3>Som ejer: Afbestilling af en booking</h3>
      <p>
        Hvis du som ejer bliver nødt til at afbestille:
      </p>

      <h4>Før du accepterer anmodningen</h4>
      <ul>
        <li>Afvis høfligt med en forklaring</li>
        <li>Lejeren kan finde et andet værktøj</li>
      </ul>

      <h4>Efter du har accepteret</h4>
      <p>
        Dette er mere alvorligt fordi lejeren har planlagt efter det:
      </p>
      <ul>
        <li>Send besked til lejeren omgående</li>
        <li>Giv en god forklaring</li>
        <li>Hjælp med at finde alternativer hvis muligt</li>
        <li>Vær forberedt på at det påvirker din rating</li>
      </ul>

      <h3>Gyldige grunde til afbestilling</h3>
      <p>
        Acceptable årsager inkluderer:
      </p>
      <ul>
        <li>Sygdom eller nødstilfælde</li>
        <li>Værktøjet er gået i stykker</li>
        <li>Uventet dobbeltbooking</li>
        <li>Ændringer i rejseplaner</li>
      </ul>

      <h3>Uacceptable afbestillinger</h3>
      <p>
        Undgå at afbestille fordi:
      </p>
      <ul>
        <li>Du fandt et billigere alternativ</li>
        <li>Du ikke gider alligevel</li>
        <li>Du glemte at du havde booket</li>
      </ul>
      <p>
        Disse påvirker din troværdighed i fællesskabet.
      </p>

      <h3>Refusionspolitik</h3>
      <p>
        DoMore's anbefalede refusionspolitik:
      </p>
      <ul>
        <li><strong>7+ dage før:</strong> 100% refusion</li>
        <li><strong>3-7 dage før:</strong> 50% refusion</li>
        <li><strong>Under 3 dage:</strong> Ingen refusion</li>
        <li><strong>Efter afhentning:</strong> Fuld betaling</li>
      </ul>
      <p>
        <em>Note: Individuelle ejere kan have deres egne politikker. Tjek altid værktøjets beskrivelse.</em>
      </p>

      <h3>Gentagne afbestillinger</h3>
      <p>
        Hvis du ofte afbestiller bookinger:
      </p>
      <ul>
        <li>Din rating vil falde</li>
        <li>Ejere kan være mindre villige til at acceptere dine anmodninger</li>
        <li>Du kan blive kontaktet af DoMore support</li>
      </ul>

      <h3>Force majeure</h3>
      <p>
        Ved ekstraordinære omstændigheder:
      </p>
      <ul>
        <li>Naturkatastrofer</li>
        <li>Regeringspåbud</li>
        <li>Alvorlige ulykker</li>
      </ul>
      <p>
        Kontakt DoMore support for særlig behandling.
      </p>

      <h3>Ejerens no-show</h3>
      <p>
        Hvis ejeren ikke dukker op ved afhentning:
      </p>
      <ul>
        <li>Vent 15 minutter</li>
        <li>Kontakt ejeren via chat og telefon</li>
        <li>Dokumentér situationen</li>
        <li>Rapportér til DoMore support</li>
        <li>Du har ret til fuld refusion</li>
      </ul>

      <h3>Lejerens no-show</h3>
      <p>
        Hvis lejeren ikke dukker op:
      </p>
      <ul>
        <li>Vent 15 minutter</li>
        <li>Forsøg at kontakte lejeren</li>
        <li>Efter 30 minutter kan du annullere</li>
        <li>Du har ret til kompensation for tabt lejeindtægt</li>
        <li>Rapportér til DoMore support</li>
      </ul>

      <h3>Tvist om afbestilling</h3>
      <p>
        Hvis I er uenige:
      </p>
      <ul>
        <li>Forsøg at kommunikere respektfuldt</li>
        <li>Henviser til booking-beskeder og aftaler</li>
        <li>Kontakt DoMore support for mægling</li>
        <li>Vi hjælper med at finde en fair løsning</li>
      </ul>

      <h3>Tips til at undgå afbestillinger</h3>
      <ul>
        <li>Book kun når du er sikker på dine planer</li>
        <li>Dobbelttjek datoer før booking</li>
        <li>Sæt påmindelser i din kalender</li>
        <li>Kommunikér med ejeren hvis du er usikker</li>
        <li>Overvej din tidsplan realistisk</li>
      </ul>

      <h3>Behov for hjælp?</h3>
      <p>
        Ved spørgsmål om afbestilling:
      </p>
      <ul>
        <li>Læs ejerens specifikke politik først</li>
        <li>Kontakt ejeren direkte via chat</li>
        <li>Kontakt DoMore support ved uenighed</li>
      </ul>
    </>
  );

  return (
    <HelpArticle
      title="Afbestillingspolitik"
      category="Værktøjsleje"
      content={content}
      relatedArticles={[
        { title: 'Bookingprocessen forklaret', link: '/help/booking-process' },
        { title: 'Forlæng din leje', link: '/help/extend-rental' },
      ]}
    />
  );
};

export default CancellationPolicy;
